import { parseSpeakerTurns } from "@rosc/contracts";
import {
  AudioLines,
  ChevronRight,
  PanelRightClose,
  PenLine,
  Plus,
  VolumeOff,
} from "lucide-react";
import type { JSX } from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import {
  V3Button,
  V3IconButton,
  v3ButtonVariants,
} from "@/components/ui/v3/button";
import { V3Drawer } from "@/components/ui/v3/drawer";
import { SegmentTranscript } from "@/components/ui/visits/SegmentTranscript";
import {
  SpeakerTranscript,
  useSpeakerLabel,
} from "@/components/ui/visits/SpeakerTranscript";
import { TranscriptEditor } from "@/components/ui/visits/TranscriptEditor";
import {
  TranscriptFlagsCount,
  TranscriptFlagsLegend,
  TranscriptFlagsProvider,
  useTranscriptFlagsFor,
} from "@/components/ui/visits/TranscriptFlags";
import {
  type PlayerRecording,
  TranscriptPlayer,
} from "@/components/ui/visits/TranscriptPlayer";
import { TranscriptV3Context } from "@/components/ui/visits/transcript-style";
import { TranscriptionsCard } from "@/components/ui/visits/transcriptions-card";
import {
  reviewTextKey,
  useTranscriptSegments,
} from "@/hooks/visits/useVisitReview";
import {
  addRecordingBlock,
  addRecordingBlockMessage,
} from "@/lib/add-recording";
import { hasMainRecording } from "@/lib/pre-chart";
import { validAudioPath } from "@/lib/review/audio-engine";
import { cn } from "@/lib/utils";
import type { VisitRecording } from "@/lib/visit-content-queries";
import type { FileData } from "@/services/files";

interface Props {
  file: FileData | null;
  multiRecordingFiles: VisitRecording[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expandedItems: string[];
  toggleExpanded: (id: string) => void;
  onAddRecording?: () => void;
  // "Edit transcript" (VITE_TRANSCRIPT_EDIT_ENABLED; latest version, nothing
  // processing).
  canEdit?: boolean;
  onTranscriptSaved?: (change: { before: string; after: string }) => void;
  // Desktop: shown inside the workspace's right pane instead of a drawer;
  // `switcher` is the pane's "Transcript | Assistant" switch.
  pane?: { switcher: React.ReactNode; onCollapse: () => void };
}

// Read again when the transcripts or the note change (status, text).
export const flagsRevision = (
  file: FileData | null,
  recordings: VisitRecording[],
): string =>
  [
    file?.status,
    reviewTextKey(file?.transcription),
    reviewTextKey(file?.result),
    ...recordings.map(
      (recording) =>
        `${recording.status}:${reviewTextKey(recording.transcription)}`,
    ),
  ].join("|");

// The transcript's first words for the row, with the same speaker labels
// as the pane (roles, or "Speaker 1" counting from one) instead of the
// stored "Speaker 0:" prefixes.
export const useSpeakerPreview = (
  transcription: string | null | undefined,
  length: number,
): string => {
  const label = useSpeakerLabel();
  const turns = parseSpeakerTurns(transcription ?? "");
  const text = turns
    .map((turn) =>
      turn.speaker === null
        ? turn.text
        : `${label(turn.speaker, turn.assignment)}: ${turn.text}`,
    )
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
};

// The entry under the visit title (iOS "Transcript ›"): opens the drawer.
export const TranscriptRow = ({
  file,
  recordings,
  transcription,
  onOpen,
}: {
  file: FileData | null;
  recordings: number;
  // The first transcript when the visit has none of its own (pre-charted).
  transcription?: string | null;
  onOpen: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const preview = useSpeakerPreview(
    transcription !== undefined ? transcription : file?.transcription,
    90,
  );
  return (
    <button
      aria-haspopup="dialog"
      className="mb-6 flex min-h-12 w-full items-center gap-3 rounded-[14px] px-4 text-left font-v3 shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-v3-brand"
      data-testid="transcript-row"
      type="button"
      onClick={onOpen}
    >
      <AudioLines aria-hidden className="size-4 shrink-0 text-v3-text-2" />
      <span className="shrink-0 text-[14px] font-semibold text-v3-text">
        {t("visit.workspace.transcript")}
      </span>
      {recordings > 1 && (
        <span className="shrink-0 text-[12.5px] text-v3-text-3">
          {t("visit.workspace.recordings", { count: recordings })}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-[13px] text-v3-text-3 max-sm:hidden">
        {preview || t("visit.workspace.transcriptPending")}
      </span>
      <ChevronRight
        aria-hidden
        className="ml-auto size-4 shrink-0 text-v3-text-3"
      />
    </button>
  );
};

// The transcript as a v3.2 drawer (right on desktop, a sheet on phones):
// every recording of the visit with its speakers and audio player, and
// "+ Add recording" when multi-recording is on.
export const TranscriptDrawer = (props: Props): JSX.Element => (
  // AI flags (VITE_TRANSCRIPT_FLAGS_ENABLED): one read for the count, the
  // legend and the marks in the text.
  <TranscriptFlagsProvider
    enabled={AppConfig.enableTranscriptFlags && !!props.file && props.open}
    revision={flagsRevision(props.file, props.multiRecordingFiles)}
    visitPublicId={props.file?.public_id}
  >
    <TranscriptV3Context.Provider value>
      <TranscriptDrawerContent {...props} />
    </TranscriptV3Context.Provider>
  </TranscriptFlagsProvider>
);

const TranscriptDrawerContent = ({
  file,
  multiRecordingFiles,
  open,
  onOpenChange,
  expandedItems,
  toggleExpanded,
  onAddRecording,
  canEdit = false,
  onTranscriptSaved,
  pane,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const isEditing = editing && canEdit && !!file;
  // Timings and stored audio per transcript (same read as the timed view,
  // cached): Edit transcript keeps each turn's time, and a recording the
  // server says has no audio gets no player.
  const timings = useTranscriptSegments(
    file?.public_id,
    flagsRevision(file, multiRecordingFiles),
    !!file && open && AppConfig.enableTranscriptPlayer === true,
  );
  const ownTimings = timings.data?.transcripts.find(
    (entry) => entry.recordingPublicId === null,
  );
  const editSegments =
    ownTimings?.state === "ready" ? ownTimings.segments : undefined;
  const noAudio = new Set(
    (timings.data?.transcripts ?? [])
      .filter((entry) => !entry.audio.available)
      .map((entry) => entry.recordingPublicId),
  );
  // VITE_TRANSCRIPT_PLAYER_ENABLED: one player for the visit's recordings.
  const players = playerRecordings(
    file,
    multiRecordingFiles,
    (number) => t("visit.player.recording", { number }),
    noAudio,
  );
  // A transcript without any stored audio (e.g. a pre-chart visit): a calm
  // note instead of a player that can only fail.
  const audioMissing =
    players.length === 0 &&
    noAudio.size > 0 &&
    ((file && hasMainRecording(file)) || multiRecordingFiles.length > 0);
  const [selectedPlayer, setSelectedPlayer] = useState(0);
  const stopEditing = useCallback(() => {
    setEditing(false);
    setAskDiscard(false);
    setDirty(false);
  }, []);
  const changeOpen = (next: boolean): void => {
    // Unsaved corrections: ask before the drawer closes.
    if (!next && isEditing && dirty) {
      setAskDiscard(true);
      return;
    }
    if (!next) stopEditing();
    onOpenChange(next);
  };
  const recordings =
    (file && hasMainRecording(file) ? 1 : 0) + multiRecordingFiles.length;
  const blocked = addRecordingBlockMessage(
    addRecordingBlock(file, multiRecordingFiles),
    t,
  );
  const drawerProps = {
    meta: (
      <span className="inline-flex items-center gap-2">
        {recordings > 1 &&
          t("visit.workspace.recordings", { count: recordings })}
        {/* Phones: the flag legend under the title already counts them. */}
        <span className="max-sm:hidden">
          <TranscriptFlagsCount />
        </span>
      </span>
    ),
    tools: (
      <>
        {canEdit && file && !isEditing && (
          <V3Button
            data-testid="transcript-edit"
            size={pane ? "icon" : "default"}
            title={t("visit.transcriptEdit.edit")}
            variant="ghost"
            onClick={() => {
              if (file && !expandedItems.includes(file.public_id))
                toggleExpanded(file.public_id);
              setEditing(true);
            }}
          >
            <PenLine aria-hidden />
            {/* Icon only on phones, so the drawer title keeps its room. */}
            <span className={pane ? "sr-only" : "max-sm:sr-only"}>
              {t("visit.transcriptEdit.edit")}
            </span>
          </V3Button>
        )}
        {onAddRecording && file && !isEditing && (
          <SubscriptionOnlyButton
            className={cn(v3ButtonVariants({ variant: "ghost", size: "icon" }))}
            disabledReason={blocked}
            validations={[
              ValidationSuscriptionButton.PLAN_FREE_TRIAL,
              ValidationSuscriptionButton.RECORDING_USAGES,
            ]}
            variant="ghost"
            onClick={onAddRecording}
          >
            <Plus />
            <span className="sr-only">
              {t("visit.workspace.addRecording")}
              {blocked && `. ${blocked}`}
            </span>
          </SubscriptionOnlyButton>
        )}
      </>
    ),
    footer:
      AppConfig.enableTranscriptPlayer && !isEditing && players.length > 0 ? (
        <TranscriptPlayer
          recordings={players}
          selected={Math.min(selectedPlayer, players.length - 1)}
          onSelect={setSelectedPlayer}
        />
      ) : AppConfig.enableTranscriptPlayer && !isEditing && audioMissing ? (
        <NoAudioNote />
      ) : undefined,
  };
  const body = (
    <>
      {onAddRecording && blocked && (
        <p
          className="px-5 pb-2 text-[12.5px] text-v3-text-3"
          data-testid="add-recording-blocked"
        >
          {blocked}
        </p>
      )}
      <div className="px-5 pb-8 [&_button]:min-h-11 lg:[&_button]:min-h-0">
        {isEditing && file ? (
          <TranscriptEditor
            askDiscard={askDiscard}
            segments={editSegments}
            text={file.transcription ?? ""}
            visitPublicId={file.public_id}
            onAskDiscardChange={setAskDiscard}
            onCancel={stopEditing}
            onDirtyChange={setDirty}
            onSaved={(change) => {
              stopEditing();
              onTranscriptSaved?.(change);
              // Close so the "Update note" bar above the note is in view.
              onOpenChange(false);
            }}
          />
        ) : AppConfig.enableTranscriptPlayer ? (
          <>
            {!pane && <TranscriptFlagsLegend className="pb-1 pt-1" />}
            <PlayerTranscripts
              file={file}
              multiRecordingFiles={multiRecordingFiles}
            />
          </>
        ) : (
          <>
            <TranscriptFlagsLegend className="pb-1 pt-1" />
            <TranscriptionsCard
              embedded
              expandedItems={expandedItems}
              file={file}
              multiRecordingFiles={multiRecordingFiles}
              toggleExpanded={toggleExpanded}
            />
          </>
        )}
      </div>
    </>
  );
  if (pane)
    return (
      <section
        aria-label={t("visit.workspace.transcript")}
        className="flex h-full min-h-0 flex-col font-v3"
        data-testid="transcript-pane"
      >
        <div className="flex h-[52px] shrink-0 items-center gap-1 pl-4 pr-2">
          {pane.switcher}
          <div className="ml-auto flex items-center">{drawerProps.tools}</div>
          {pane.onCollapse && (
            <V3IconButton
              data-testid="pane-collapse"
              label={t("v3.pane.hide")}
              onClick={() => {
                if (isEditing && dirty) setAskDiscard(true);
                else pane.onCollapse();
              }}
            >
              <PanelRightClose />
            </V3IconButton>
          )}
        </div>
        {!isEditing && (
          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 px-5 pb-1 text-[12.5px] text-v3-text-3">
            {recordings > 1 &&
              t("visit.workspace.recordings", { count: recordings })}
            <TranscriptFlagsLegend />
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
        {drawerProps.footer && (
          <div className="shrink-0 px-3 pb-3">{drawerProps.footer}</div>
        )}
      </section>
    );
  return (
    <V3Drawer
      footer={drawerProps.footer}
      meta={drawerProps.meta}
      open={open}
      // Phones: a tall bottom sheet (about 85% of the screen), not one sized
      // to its first rows.
      className="max-sm:h-[85dvh]"
      testId="transcript-drawer"
      title={t("visit.workspace.transcript")}
      tools={drawerProps.tools}
      width="wide"
      onOpenChange={changeOpen}
    >
      {body}
    </V3Drawer>
  );
};

// The visit's recordings with stored audio, for the player. `noAudio`: the
// transcripts the server reports without audio (null: the visit's own).
const playerRecordings = (
  file: FileData | null,
  recordings: VisitRecording[],
  label: (number: number) => string,
  noAudio: ReadonlySet<string | null> = new Set(),
): PlayerRecording[] => {
  const all = [
    ...(file?.name && hasMainRecording(file) && !noAudio.has(null)
      ? [{ path: file.name }]
      : []),
    ...recordings.flatMap((recording) =>
      recording.name && !noAudio.has(recording.public_id)
        ? [{ path: recording.name }]
        : [],
    ),
  ].filter((item) => validAudioPath(item.path));
  return all.map((item, index) => ({
    ...item,
    label: label(index + 1),
  }));
};

// v3.2 empty state under the transcript when the visit has no audio.
export const NoAudioNote = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <p
      className="flex items-center gap-2.5 border-t border-v3-border px-2 pt-3 pb-1 font-v3 text-[13.5px] text-v3-text-3"
      data-testid="transcript-no-audio"
      role="status"
    >
      <span
        aria-hidden
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-v3-surface-2 text-v3-text-3"
      >
        <VolumeOff className="size-4" />
      </span>
      {t("visit.audioPlayer.noAudio")}
    </p>
  );
};

// Each recording's transcript as timed segments (tap to play from there)
// when the server has timings for it, otherwise as speaker turns.
const PlayerTranscripts = ({
  file,
  multiRecordingFiles,
}: {
  file: FileData | null;
  multiRecordingFiles: VisitRecording[];
}): JSX.Element => {
  const { t } = useTranslation();
  const segments = useTranscriptSegments(
    file?.public_id,
    flagsRevision(file, multiRecordingFiles),
    !!file,
  );
  const items = [
    ...(file && hasMainRecording(file)
      ? [
          {
            key: file.public_id,
            recordingPublicId: null as string | null,
            text: file.transcription ?? "",
            path: file.name,
            processing: false,
          },
        ]
      : []),
    ...multiRecordingFiles.map((recording) => ({
      key: recording.public_id,
      recordingPublicId: recording.public_id as string | null,
      text: recording.transcription ?? "",
      path: recording.name,
      processing: recording.status?.toLowerCase() === "processing",
    })),
  ];
  return (
    <div className="space-y-5 pt-2">
      {items.map((item, index) => {
        const timed = segments.data?.transcripts.find(
          (entry) => entry.recordingPublicId === item.recordingPublicId,
        );
        return (
          <section key={item.key} data-testid="player-transcript">
            {items.length > 1 && (
              <h3 className="mb-1.5 font-v3 text-[13px] font-semibold uppercase tracking-[0.04em] text-v3-text-3">
                {t("visit.player.recording", { number: index + 1 })}
              </h3>
            )}
            {item.processing ? (
              <p className="py-3 text-[14px] text-v3-text-3">
                {t("visit.workspace.transcriptPending")}
              </p>
            ) : timed?.state === "ready" && timed.segments.length > 0 ? (
              <TimedTranscript
                path={item.path && validAudioPath(item.path) ? item.path : null}
                recordingPublicId={item.recordingPublicId}
                segments={timed.segments}
                text={item.text}
              />
            ) : (
              <div className="space-y-2">
                <SpeakerTranscript
                  recordingPublicId={item.recordingPublicId}
                  text={item.text}
                />
                {/* Without audio there is nothing to play: the drawer's
                    "No audio" note says so instead. */}
                {segments.isFetched && timed?.audio.available !== false && (
                  <p
                    className="text-[12.5px] text-v3-text-3"
                    data-testid="transcript-no-timings"
                  >
                    {t("visit.player.noTimings")}
                  </p>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

const TimedTranscript = ({
  segments,
  text,
  path,
  recordingPublicId,
}: {
  segments: Parameters<typeof SegmentTranscript>[0]["segments"];
  text: string;
  path: string | null;
  recordingPublicId: string | null;
}): JSX.Element => {
  const flags = useTranscriptFlagsFor(recordingPublicId);
  return (
    <SegmentTranscript
      audioPath={path}
      flags={flags}
      segments={segments}
      transcript={text}
    />
  );
};
